import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { X, Send, Users } from "lucide-react";
import { fetchMessages, sendMessage } from "@/lib/mapioApi";
import { useAuth } from "@/contexts/AuthContext";

export default function ChatPanel({ event, onClose }) {
  const { user: authUser, profile } = useAuth();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [guestName, setGuestName] = useState(() => localStorage.getItem("mapio_user") || "Guest");
  const [showMembers, setShowMembers] = useState(false);
  const scrollRef = useRef(null);
  const [guestId] = useState(() => {
    const existing = localStorage.getItem("mapio_chat_guest_id");
    if (existing) return existing;
    const browserCrypto = window.crypto || window.msCrypto;
    const created = `guest-${browserCrypto?.randomUUID?.() || Math.random().toString(36).slice(2)}`;
    localStorage.setItem("mapio_chat_guest_id", created);
    return created;
  });

  const currentUserId = authUser?.id || guestId;
  const currentUserName = profile?.username || authUser?.username || authUser?.email?.split("@")[0] || guestName;

  const load = async () => {
    try {
      const data = await fetchMessages(event.id);
      setMessages(data);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event.id]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSend = async (e) => {
    e?.preventDefault?.();
    const msg = text.trim();
    if (!msg) return;
    if (!authUser) localStorage.setItem("mapio_user", guestName);
    setText("");
    try {
      const created = await sendMessage(event.id, {
        user_id: currentUserId,
        user_name: currentUserName,
        message: msg,
        is_host: false,
      });
      setMessages((m) => [...m, created]);
    } catch (err) {
      console.error(err);
    }
  };

  const members = [event.host_name, ...(event.members || [])];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[800] bg-black/30 flex items-end sm:items-center justify-center"
      onClick={onClose}
      data-testid="chat-panel"
    >
      <motion.div
        initial={{ y: 40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 40, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-white w-full sm:w-[420px] sm:h-[640px] h-[85vh] sm:rounded-3xl rounded-t-3xl shadow-2xl flex flex-col overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-100">
          <img
            src={event.logo_image}
            alt=""
            className="w-9 h-9 rounded-full object-cover bg-gray-100"
          />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-gray-900 truncate" style={{ fontFamily: "Manrope" }}>
              {event.name}
            </p>
            <button
              onClick={() => setShowMembers((s) => !s)}
              className="text-xs text-gray-500 hover:text-blue-600 flex items-center gap-1"
            >
              <Users className="w-3 h-3" />
              {members.length} member{members.length === 1 ? "" : "s"}
            </button>
          </div>
          <button
            data-testid="chat-close-btn"
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {showMembers && (
          <div className="px-4 py-2 border-b border-gray-100 bg-gray-50 max-h-32 overflow-y-auto">
            {members.map((m, i) => (
              <div key={i} className="flex items-center gap-2 py-1 text-xs">
                <div className="w-6 h-6 rounded-full bg-gradient-to-br from-blue-500 to-violet-500 text-white flex items-center justify-center font-semibold text-[10px]">
                  {m.charAt(0).toUpperCase()}
                </div>
                <span className="text-gray-700">
                  {m} {i === 0 && <span className="text-blue-600 font-semibold">· Host</span>}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Messages */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-[#FAFAFC]">
          {messages.length === 0 && (
            <p className="text-center text-xs text-gray-400 mt-6">
              Be the first to say hi 👋
            </p>
          )}
          {messages.map((m) => {
            const mine = m.user_id ? m.user_id === currentUserId : m.user_name === currentUserName;
            return (
              <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                <div className="max-w-[78%]">
                  {!mine && (
                    <p className="text-[10px] text-gray-500 mb-0.5 ml-2">
                      {m.user_name}
                      {m.is_host && <span className="text-blue-600 ml-1">· Host</span>}
                    </p>
                  )}
                  <div
                    className={`px-3.5 py-2 rounded-2xl text-sm ${
                      mine
                        ? "bg-gradient-to-br from-blue-600 to-violet-600 text-white rounded-br-sm"
                        : "bg-white border border-gray-100 text-gray-900 rounded-bl-sm"
                    }`}
                  >
                    {m.message}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Composer */}
        <form onSubmit={handleSend} className="p-3 border-t border-gray-100 flex items-center gap-2 bg-white">
          <input
            data-testid="chat-username-input"
            value={currentUserName}
            onChange={(e) => setGuestName(e.target.value)}
            placeholder="Your name"
            disabled={!!authUser}
            className="w-24 px-3 py-2 text-xs rounded-full bg-gray-100 outline-none focus:bg-white focus:ring-2 focus:ring-blue-200 disabled:text-gray-500"
          />
          <input
            data-testid="chat-message-input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Message…"
            className="flex-1 px-4 py-2.5 text-sm rounded-full bg-gray-100 outline-none focus:bg-white focus:ring-2 focus:ring-blue-200"
          />
          <button
            data-testid="chat-send-btn"
            type="submit"
            disabled={!text.trim()}
            className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-600 to-violet-600 text-white flex items-center justify-center disabled:opacity-40"
          >
            <Send className="w-4 h-4" />
          </button>
        </form>
      </motion.div>
    </motion.div>
  );
}
